import { IProduct, StoredBuildV2 } from "../types";

const STORAGE_VERSION = 2;
const STORAGE_KEY_PREFIX = "srb_build_";

function getStorageKey(creatorSlug: string): string {
  return `${STORAGE_KEY_PREFIX}${creatorSlug || "default"}`;
}

function isLocalStorageAvailable(): boolean {
  try {
    const testKey = "__srb_test__";
    localStorage.setItem(testKey, "1");
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

export function saveToLocalStorage(
  components: Record<string, IProduct>,
  creatorSlug: string
): boolean {
  if (!isLocalStorageAvailable()) {
    console.warn("[STORAGE] localStorage not available, skipping persistence");
    return false;
  }

  try {
    const key = getStorageKey(creatorSlug);

    // If no components, remove the stored build
    if (Object.keys(components).length === 0) {
      localStorage.removeItem(key);
      return true;
    }

    const storedBuild: StoredBuildV2 = {
      version: STORAGE_VERSION,
      creatorSlug,
      components,
      savedAt: Date.now(),
    };

    localStorage.setItem(key, JSON.stringify(storedBuild));
    return true;
  } catch (error) {
    console.error("[STORAGE] Failed to save build:", error);
    return false;
  }
}

export function loadFromLocalStorage(
  creatorSlug: string
): Record<string, IProduct> | null {
  if (!isLocalStorageAvailable()) {
    console.warn("[STORAGE] localStorage not available");
    return null;
  }

  try {
    const key = getStorageKey(creatorSlug);
    const data = localStorage.getItem(key);

    if (!data) {
      return null;
    }

    const parsed = JSON.parse(data);

    // Handle version 2 format (full product data)
    if (parsed.version === 2 && parsed.components) {
      return parsed.components;
    }

    // Handle legacy version 1 format - return null and let it be cleared
    // (we can't restore without API calls, and the old approach was broken)
    if (parsed.version === 1) {
      console.warn(
        "[STORAGE] Legacy v1 format detected, clearing stored build"
      );
      localStorage.removeItem(key);
      return null;
    }

    return null;
  } catch (error) {
    console.error("[STORAGE] Failed to load build:", error);
    return null;
  }
}

export function clearBuild(creatorSlug: string): boolean {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    const key = getStorageKey(creatorSlug);
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error("[STORAGE] Failed to clear build:", error);
    return false;
  }
}
